import React from 'react';
import { Box, Typography } from '@mui/material';
import PageContainer from '../layout/PageContainer';

// Constants for reusable styles and content
const STYLES = {
    container: {
        bgcolor: 'linear-gradient(90deg, #181C1F 0%, #23272A 100%)',
        minHeight: '100vh',
        py: { xs: 3, md: 3 }
    },
    headerSection: {
        textAlign: 'center',
        mb: { xs: 4, md: 6 }
    },
    title: {
        fontSize: { xs: '24px', sm: '2.5rem', md: '2.5rem', lg: '3rem' },
        fontWeight: 700,
        color: '#fff',
        mb: 2,
        fontFamily: "'Wix Madefor Display', sans-serif"
    },
    contentSection: {
        mb: { xs: 3, md: 4 }
    },
    sectionTitle: {
        fontSize: {xs:'12px', sm:'16px', md:'20px'},
        fontWeight: 700,
        fontStyle: 'normal',
        lineHeight: {xs:'20px', md:'28px'},
        letterSpacing: '0%',
        color: '#fff',
        mb: { xs: 2, md: 3 },
        fontFamily: "'Wix Madefor Display', sans-serif"
    },
    paragraph: {
        fontSize: {xs:'12px', sm:'16px', md:'20px'},
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: {xs:'20px', md:'28px'},
        letterSpacing: '0%',
        color: 'rgba(200, 200, 200, 1)',
        mb: { xs: 2, md: 3 },
        fontFamily: "'Wix Madefor Display', sans-serif"
    },
    listItem: {
        fontSize: {xs:'12px', sm:'16px', md:'20px'},
        fontWeight: 400,
        fontStyle: 'normal',
        lineHeight: {xs:'20px', md:'28px'},
        letterSpacing: '0%',
        color: 'rgba(200, 200, 200, 1)',
        mb: 1,
        fontFamily: "'Wix Madefor Display', sans-serif",
        pl: 2
    },
    linkText: {
        color: 'rgba(134, 230, 191, 1)',
        textDecoration: 'none',
        fontFamily: "'Wix Madefor Display', sans-serif",
        '&:hover': {
            textDecoration: 'underline'
        }
    }
};

const TERMS_CONTENT = {
    introText: "Welcome to Rubaai Technologies (Pvt) Ltd (\"Rubaai\", \"we\", \"our\", or \"us\"). These Terms of Service (\"Terms\") govern your access to and use of our products, services, and platforms, including but not limited to:",
    lastUpdated: "Last updated: 04-08-2025",
    sections: [
        {
            title: "1. Introduction",
            content: [
                "Welcome to Rubaai Technologies (Pvt) Ltd (\"Rubaai\", \"we\", \"our\", or \"us\"). These Terms of Service (\"Terms\") govern your access to and use of our products, services, and platforms, including but not limited to:"
            ],
            listItems: [
                "MoiPay – Our digital payment platform",
                "Rubaai POS – Our cloud-based Point-of-Sale system",
                "Rubaai EMS – Our Employee Management System"
            ],
            additionalContent: [
                "By accessing or using our Services, you agree to be bound by these Terms, along with our Privacy Policy. If you do not agree, please refrain from using our Services."
            ]
        },
        {
            title: "2. Acceptance of Terms",
            content: [
                "By creating an account, accessing, or using any of our Services, you acknowledge that you:"
            ],
            listItems: [
                "Have read and understood these Terms;",
                "Agree to comply with all applicable laws and regulations;",
                "Accept that additional terms may apply to certain features or integrations."
            ]
        },
        {
            title: "3. Eligibility",
            content: [
                "You may use our Services only if you:"
            ],
            listItems: [
                "Are at least 18 years old.",
                "Have the legal authority to enter into binding agreements;",
                "Are not prohibited by applicable law from using our Services."
            ]
        },
        {
            title: "4. Account Registration & Responsibilities",
            content: [
                "Some Services may require account registration. You agree to:"
            ],
            listItems: [
                "Provide accurate and complete information during sign-up;",
                "Keep your credentials confidential;",
                "Notify us immediately of any unauthorized use of your account."
            ],
            additionalContent: [
                "You are responsible for all activities carried out through your account."
            ]
        },
        {
            title: "5. Use of Services",
            content: [
                "You agree to use our Services only for lawful purposes and in accordance with these Terms. When using MoiPay, POS, and EMS, you must:"
            ],
            listItems: [
                "Ensure transaction accuracy and data integrity;",
                "Comply with all applicable financial, tax, and employment laws;",
                "Keep your business records updated and compliant with regulations."
            ]
        },
        {
            title: "6. User Data & Security",
            content: [
                "We implement strong security measures including encryption and secure storage to protect your data. You are responsible for:"
            ],
            listItems: [
                "Securing your personal devices and networks;",
                "Using updated antivirus and anti-malware tools;",
                "Reporting any suspected security breaches immediately."
            ]
        },
        {
            title: "7. Third-Party Services & Integrations",
            content: [
                "Our Services may integrate with third-party platforms including banks, payment gateways, and cloud providers. We are not responsible for the performance, security, or terms of these third-party services. You must review and agree to their terms before use."
            ]
        },
        {
            title: "8. Payments, Fees & Transactions",
            content: [
                "Payment terms vary by service:"
            ],
            listItems: [
                "MoiPay: All transactions are processed under agreements with partner banks and payment providers. Transaction fees, settlement times, and limits may apply;",
                "POS: Merchants are responsible for pricing, inventory accuracy, and tax compliance;",
                "EMS: Subscription fees are billed as per the selected plan."
            ],
            additionalContent: [
                "Fees are subject to change with prior notice. Refunds follow our stated refund policy. Valid identification and business documents may be requested for KYC/AML compliance. Transactions may be monitored for suspicious activity as per anti-money laundering laws."
            ]
        },
        {
            title: "9. Service Availability & Downtime",
            content: [
                "While we aim for high uptime, we cannot guarantee uninterrupted access. Scheduled maintenance, technical issues, or events beyond our control may cause temporary service interruptions."
            ]
        },
        {
            title: "10. Prohibited Activities",
            content: [
                "You may not:"
            ],
            listItems: [
                "Engage in fraudulent, illegal, or misleading activities;",
                "Violate intellectual property rights;",
                "Upload malicious software or harmful code;",
                "Interfere with security systems or operations."
            ]
        },
        {
            title: "11. Intellectual Property Rights",
            content: [
                "All trademarks, software, logos, and other intellectual property related to Rubaai Technologies' Services remain our property. You may not copy, modify, or distribute them without our written consent."
            ]
        },
        {
            title: "12. Indemnification",
            content: [
                "You agree to indemnify and hold harmless Rubaai Technologies, its officers, and employees from any claims, damages, or expenses arising from misuse of Services or breach of these Terms."
            ]
        },
        {
            title: "13. Limitation of Liability",
            content: [
                "To the maximum extent permitted by law, Rubaai Technologies shall not be liable for indirect, incidental, or consequential damages resulting from the use of our Services."
            ]
        },
        {
            title: "14. Force Majeure",
            content: [
                "Rubaai Technologies is not liable for delays or failures caused by events beyond our reasonable control, including natural disasters, power outages, government actions, or internet failures."
            ]
        },
        {
            title: "15. Termination",
            content: [
                "Rubaai Technologies reserves the right to suspend or terminate your access if these Terms or applicable laws are violated."
            ]
        },
        {
            title: "16. Governing Law & Dispute Resolution",
            content: [
                "These Terms are governed by the laws of Sri Lanka. Any disputes will be resolved under the exclusive jurisdiction of Sri Lankan courts."
            ]
        },
        {
            title: "17. Changes to Terms",
            content: [
                "Rubaai Technologies may update these Terms from time to time. Continued use of Services after changes are posted constitutes acceptance of the revised Terms."
            ]
        },
        {
            title: "18. Contact Information",
            content: [
                "For questions regarding these Terms, please contact us:"
            ],
            listItems: [
                "Company Name: Rubaai Technologies (Pvt) Ltd",
                "Email: support@rubaai.net",
                "Phone: +94 77 444 3272",
                "Address: No.08, Thiruvalluvar Supermarket, Bus Stand Building, Mannar Town, Mannar"
            ]
        }
    ]
};

const TermsOfServiceComponent = () => {
    return (
        <Box sx={STYLES.container}>
            <PageContainer>
                {/* Header Section */}
                <Box sx={STYLES.headerSection}>
                    <Typography sx={STYLES.title}>
                        Terms of Service
                    </Typography>
                </Box>

                {/* Introduction Text */}
                <Box sx={STYLES.contentSection}>
                    <Typography sx={STYLES.paragraph}>
                        {TERMS_CONTENT.introText}
                    </Typography>
                    <Typography sx={{
                        ...STYLES.paragraph,
                        mt: 3,
                        mb: 4
                    }}>
                        {TERMS_CONTENT.lastUpdated}
                    </Typography>
                </Box>

                {/* Content Sections */}
                {TERMS_CONTENT.sections.map((section, index) => (
                    <Box key={index} sx={STYLES.contentSection}>
                        <Typography sx={STYLES.sectionTitle}>
                            {section.title}
                        </Typography>
                        
                        {section.content && section.content.map((paragraph, pIndex) => (
                            <Typography key={pIndex} sx={STYLES.paragraph}>
                                {paragraph}
                            </Typography>
                        ))}
                        
                        {section.listItems && (
                            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                                {section.listItems.map((item, lIndex) => (
                                    <Typography 
                                        key={lIndex} 
                                        component="li" 
                                        sx={STYLES.listItem}
                                    >
                                        {item}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {section.additionalContent && section.additionalContent.map((paragraph, aIndex) => (
                            <Typography key={aIndex} sx={STYLES.paragraph}>
                                {paragraph}
                            </Typography>
                        ))}
                    </Box>
                ))}
            </PageContainer>
        </Box>
    );
};

export default TermsOfServiceComponent;
